import type {
  DocumentFieldDefinition,
  DocumentSectionDefinition,
} from '@/features/case-workspace/types'

const ISO_DATE_RULE = {
  type: 'pattern' as const,
  value: '^\\d{4}-\\d{2}-\\d{2}$',
  message: 'Use YYYY-MM-DD format.',
}

const NUMERIC_RULE = {
  type: 'pattern' as const,
  value: '^\\d+(\\.\\d+)?$',
  message: 'Use a numeric value.',
}

const PORT_RULE = {
  type: 'pattern' as const,
  value: '^\\d{4}$',
  message: 'Use the four digit U.S. port code format when a CBP port code is available.',
}

const CONTAINER_RULE = {
  type: 'custom' as const,
  message: 'Use ISO-style container numbers where possible, separated by commas if more than one applies.',
}

const BILL_RULE = {
  type: 'pattern' as const,
  value: '^[A-Z0-9\\-\\/]{3,40}$',
  message: 'Use a normalized bill number with letters, numbers, dashes, or slashes.',
}

const YES_NO_OPTIONS = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
]

function field(definition: DocumentFieldDefinition) {
  return definition
}

export const MANIFEST_1302_INTAKE_SOURCE_OPTIONS = [
  { value: 'ace_direct_transmission', label: 'ACE Direct Transmission' },
  { value: 'port_arrival_document', label: 'Port Arrival Document / Paper Form' },
  { value: 'vecs_submission', label: 'VECS Submission' },
  { value: 'uploaded_external_source', label: 'Uploaded by User / External Source' },
  { value: 'unknown_needs_review', label: 'Unknown / Needs Review' },
]

export const MANIFEST_1302_SUBMISSION_METHOD_OPTIONS = [
  { value: 'electronic', label: 'Electronic submission' },
  { value: 'paper_presented', label: 'Paper form presented on arrival' },
  { value: 'hybrid', label: 'Hybrid / mixed source' },
  { value: 'unknown', label: 'Unknown / needs review' },
]

export const MANIFEST_1302_TRANSMISSION_STATUS_OPTIONS = [
  { value: 'received', label: 'Received' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'amended', label: 'Amended' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'rejected', label: 'Rejected / incomplete' },
]

export const MANIFEST_1302_MODE_OPTIONS = [
  { value: '11', label: '11 — Vessel, container' },
  { value: '10', label: '10 — Vessel, non-container' },
  { value: 'unknown', label: 'Unknown / needs review' },
]

export const MANIFEST_1302_STATUS_OPTIONS = [
  { value: 'original', label: 'Original manifest' },
  { value: 'amended', label: 'Amended manifest' },
  { value: 'supplemental', label: 'Supplemental filing' },
  { value: 'unknown', label: 'Unknown / needs review' },
]

export const MANIFEST_1302_HOLD_STATUS_OPTIONS = [
  { value: 'none', label: 'No hold or exam' },
  { value: 'hold', label: 'Hold' },
  { value: 'exam', label: 'Exam required' },
  { value: 'released', label: 'Released' },
  { value: 'unknown', label: 'Unknown / needs review' },
]

export const MANIFEST_1302_WEIGHT_UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'mt', label: 'Metric tons (mt)' },
]

export const MANIFEST_1302_DISCREPANCY_OPTIONS = [
  { value: 'exact_match', label: 'Exact match' },
  { value: 'likely_match', label: 'Likely match' },
  { value: 'missing_from_other_document', label: 'Missing from one document' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'needs_manual_review', label: 'Needs manual review' },
]

export const MANIFEST_1302_RISK_OPTIONS = [
  { value: 'informational', label: 'Informational' },
  { value: 'low', label: 'Low risk' },
  { value: 'moderate', label: 'Moderate risk' },
  { value: 'high', label: 'High risk' },
  { value: 'blocks_acceptance', label: 'Blocks acceptance / transmit' },
]

export const MANIFEST_1302_SECTIONS: DocumentSectionDefinition[] = [
  {
    id: 'manifest_source_submission_status',
    title: 'Manifest Source & Submission Status',
    description: 'Capture how the manifest entered the case, what system or document it came from, and how confident the reviewer should be in the structured values.',
    fields: [
      field({
        key: 'manifest_intake_source',
        label: 'Manifest Intake Source',
        officialName: 'Manifest Intake Source',
        type: 'select',
        required: true,
        options: MANIFEST_1302_INTAKE_SOURCE_OPTIONS,
        aceMappingId: 'manifest1302.intake_source',
        helpText: 'CBP notes that most imported-merchandise cargo declarations are received electronically in ACE, while remaining paper functions are moving into VECS.',
        validation: [{ type: 'required', message: 'Manifest intake source is required.' }],
      }),
      field({
        key: 'source_channel',
        label: 'Source Channel',
        officialName: 'Source Channel',
        type: 'text',
        placeholder: 'ACE, VECS, paper packet, port upload',
        aceMappingId: 'manifest1302.source_channel',
      }),
      field({
        key: 'submission_method',
        label: 'Submission Method',
        officialName: 'Submission Method',
        type: 'select',
        options: MANIFEST_1302_SUBMISSION_METHOD_OPTIONS,
        aceMappingId: 'manifest1302.submission_method',
      }),
      field({
        key: 'document_provenance',
        label: 'Document Provenance',
        officialName: 'Document Provenance',
        type: 'textarea',
        placeholder: 'Describe the source of the manifest record or uploaded artifact.',
        aceMappingId: 'manifest1302.document_provenance',
      }),
      field({
        key: 'transmission_status',
        label: 'Transmission Status',
        officialName: 'Transmission Status',
        type: 'select',
        options: MANIFEST_1302_TRANSMISSION_STATUS_OPTIONS,
        aceMappingId: 'manifest1302.transmission_status',
      }),
      field({
        key: 'received_timestamp',
        label: 'Received Timestamp',
        officialName: 'Received Timestamp',
        type: 'text',
        placeholder: 'YYYY-MM-DD HH:MM',
        aceMappingId: 'manifest1302.received_timestamp',
      }),
      field({
        key: 'port_intake_timestamp',
        label: 'Port Intake Timestamp',
        officialName: 'Port Intake Timestamp',
        type: 'text',
        placeholder: 'YYYY-MM-DD HH:MM',
        aceMappingId: 'manifest1302.port_intake_timestamp',
      }),
      field({
        key: 'uploaded_file_reference',
        label: 'Uploaded File / System Source Reference',
        officialName: 'Uploaded File / System Source Reference',
        type: 'text',
        placeholder: 'Source file name or system reference',
        aceMappingId: 'manifest1302.source_reference',
      }),
    ],
  },
  {
    id: 'vessel_voyage_arrival',
    title: 'Vessel / Voyage / Arrival',
    description: 'Record the vessel, voyage, master, carrier, and arrival header details represented by CBP Form 1302.',
    fields: [
      field({
        key: 'manifest_number',
        label: 'Manifest Number',
        officialName: 'Manifest No.',
        type: 'text',
        required: true,
        aceMappingId: 'manifest1302.manifest_number',
        validation: [{ type: 'required', message: 'Manifest number is required.' }],
      }),
      field({
        key: 'vessel_name',
        label: 'Vessel Name',
        officialName: 'Vessel Name',
        type: 'text',
        required: true,
        aceMappingId: 'manifest1302.vessel_name',
        validation: [{ type: 'required', message: 'Vessel name is required.' }],
      }),
      field({
        key: 'vessel_nationality',
        label: 'Vessel Nationality',
        officialName: 'Nationality',
        type: 'text',
        placeholder: 'Flag state / nationality',
        aceMappingId: 'manifest1302.vessel_nationality',
      }),
      field({
        key: 'imo_number',
        label: 'IMO Number',
        officialName: 'International Maritime Organization (IMO) Number',
        type: 'text',
        placeholder: 'Seven-digit IMO number',
        aceMappingId: 'manifest1302.imo_number',
        validation: [{ type: 'pattern', value: '^\\d{7}$', message: 'Use the seven digit IMO number.' }],
      }),
      field({
        key: 'voyage_number',
        label: 'Voyage Number',
        officialName: 'Voyage Number',
        type: 'text',
        required: true,
        aceMappingId: 'manifest1302.voyage_number',
        validation: [{ type: 'required', message: 'Voyage number is required.' }],
      }),
      field({
        key: 'masters_name',
        label: 'Master’s Name',
        officialName: 'Master’s Name',
        type: 'text',
        aceMappingId: 'manifest1302.masters_name',
      }),
      field({
        key: 'carrier_name',
        label: 'Carrier Name',
        officialName: 'Carrier Name',
        type: 'text',
        aceMappingId: 'manifest1302.carrier_name',
      }),
      field({
        key: 'carrier_code_scac',
        label: 'Carrier Code / SCAC',
        officialName: 'Carrier Code / SCAC',
        type: 'text',
        aceMappingId: 'manifest1302.carrier_code_scac',
      }),
      field({
        key: 'arrival_date',
        label: 'Arrival Date',
        officialName: 'Arrival Date',
        type: 'date',
        aceMappingId: 'manifest1302.arrival_date',
        validation: [ISO_DATE_RULE],
      }),
      field({
        key: 'arrival_reference',
        label: 'Entry / Arrival Reference',
        officialName: 'Entry / Arrival Reference',
        type: 'text',
        aceMappingId: 'manifest1302.arrival_reference',
      }),
      field({
        key: 'mode_of_transport',
        label: 'Mode of Transport',
        officialName: 'Mode of Transport',
        type: 'select',
        options: MANIFEST_1302_MODE_OPTIONS,
        required: true,
        aceMappingId: 'manifest1302.mode_of_transport',
        validation: [{ type: 'required', message: 'Mode of transport is required.' }],
      }),
      field({
        key: 'manifest_status',
        label: 'Manifest Status',
        officialName: 'Manifest Status',
        type: 'select',
        options: MANIFEST_1302_STATUS_OPTIONS,
        aceMappingId: 'manifest1302.manifest_status',
      }),
      field({
        key: 'amendment_status',
        label: 'Amendment Status',
        officialName: 'Amendment Status',
        type: 'text',
        placeholder: 'Original, amended, supplemental',
        aceMappingId: 'manifest1302.amendment_status',
      }),
    ],
  },
  {
    id: 'ports_routing',
    title: 'Ports & Routing',
    description: 'Track the last foreign port, foreign port of lading, first U.S. arrival, discharge port, and movement details associated with the inward cargo declaration.',
    fields: [
      field({
        key: 'last_foreign_port_before_us',
        label: 'Last Foreign Port Before U.S.',
        officialName: 'Last Foreign Port Before U.S.',
        type: 'text',
        required: true,
        aceMappingId: 'manifest1302.last_foreign_port_before_us',
        validation: [{ type: 'required', message: 'Last foreign port before the U.S. is required.' }],
      }),
      field({
        key: 'foreign_port_of_lading',
        label: 'Foreign Port of Lading',
        officialName: 'Foreign Port of Lading',
        type: 'text',
        aceMappingId: 'manifest1302.foreign_port_of_lading',
      }),
      field({
        key: 'us_port_of_arrival',
        label: 'First U.S. Port of Arrival',
        officialName: 'First U.S. Port of Arrival',
        type: 'text',
        placeholder: 'Four digit U.S. port code',
        aceMappingId: 'manifest1302.us_port_of_arrival',
        validation: [PORT_RULE],
      }),
      field({
        key: 'us_port_of_discharge',
        label: 'U.S. Port of Discharge / Unlading',
        officialName: 'Port of Discharge',
        type: 'text',
        required: true,
        placeholder: 'Four digit U.S. port code',
        aceMappingId: 'manifest1302.us_port_of_discharge',
        validation: [
          { type: 'required', message: 'U.S. port of discharge is required.' },
          PORT_RULE,
        ],
      }),
      field({
        key: 'final_destination',
        label: 'Final Destination',
        officialName: 'Final Destination',
        type: 'text',
        aceMappingId: 'manifest1302.final_destination',
      }),
      field({
        key: 'in_bond_indicator',
        label: 'In-Bond Indicator',
        officialName: 'In-Bond Indicator',
        type: 'select',
        options: YES_NO_OPTIONS,
        aceMappingId: 'manifest1302.in_bond_indicator',
      }),
      field({
        key: 'it_number',
        label: 'I.T. Number',
        officialName: 'I.T. Number',
        type: 'text',
        conditional: true,
        visibleWhen: [{ fieldKey: 'in_bond_indicator', operator: 'equals', value: 'Y' }],
        aceMappingId: 'manifest1302.it_number',
      }),
      field({
        key: 'general_order_indicator',
        label: 'General Order Indicator',
        officialName: 'General Order Indicator',
        type: 'select',
        options: YES_NO_OPTIONS,
        aceMappingId: 'manifest1302.general_order_indicator',
      }),
      field({
        key: 'hold_exam_release_status',
        label: 'Hold / Exam / Release Status',
        officialName: 'Hold / Exam / Release Status',
        type: 'select',
        options: MANIFEST_1302_HOLD_STATUS_OPTIONS,
        aceMappingId: 'manifest1302.hold_exam_release_status',
      }),
      field({
        key: 'arrival_to_discharge_notes',
        label: 'Arrival-to-Discharge Movement Notes',
        officialName: 'Arrival-to-Discharge Movement Notes',
        type: 'textarea',
        aceMappingId: 'manifest1302.arrival_to_discharge_notes',
      }),
    ],
  },
  {
    id: 'bill_shipment_details',
    title: 'Bill / Shipment Details',
    description: 'Review each bill or shipment line from the inward cargo declaration in structured form rather than as an unparsed text blob.',
    kind: 'line_items',
    fields: [
      field({
        key: 'shipment_registry',
        label: 'Shipment registry',
        type: 'text',
        editable: false,
        aceMappingId: 'manifest1302.shipment_rows',
        helpText: 'Rendered through the dedicated Manifest shipment-row editor.',
      }),
    ],
  },
  {
    id: 'validation_reconciliation',
    title: 'Validation & Reconciliation',
    description: 'Capture cross-document match status, explain conflicts, and record what still needs manual review before acceptance or transmit.',
    fields: [
      field({
        key: 'cross_document_discrepancy_status',
        label: 'Cross-Document Discrepancy Status',
        officialName: 'Cross-Document Discrepancy Status',
        type: 'select',
        options: MANIFEST_1302_DISCREPANCY_OPTIONS,
        aceMappingId: 'manifest1302.cross_document_discrepancy_status',
      }),
      field({
        key: 'risk_severity',
        label: 'Risk Severity',
        officialName: 'Risk Severity',
        type: 'select',
        options: MANIFEST_1302_RISK_OPTIONS,
        aceMappingId: 'manifest1302.risk_severity',
      }),
      field({
        key: 'validation_status',
        label: 'Validation Status',
        officialName: 'Validation Status',
        type: 'text',
        aceMappingId: 'manifest1302.validation_status',
      }),
      field({
        key: 'reconciliation_summary',
        label: 'Reconciliation Summary',
        officialName: 'Reconciliation Summary',
        type: 'textarea',
        aceMappingId: 'manifest1302.reconciliation_summary',
      }),
      field({
        key: 'awa_review_summary',
        label: 'AWA Review Summary',
        officialName: 'AWA Review Summary',
        type: 'textarea',
        aceMappingId: 'manifest1302.awa_review_summary',
      }),
    ],
  },
  {
    id: 'audit_history',
    title: 'Audit / History',
    description: 'Track provenance, confidence, confirmation state, and amendment history for the manifest record.',
    fields: [
      field({
        key: 'extraction_source',
        label: 'Extraction Source',
        officialName: 'Extraction Source',
        type: 'text',
        aceMappingId: 'manifest1302.extraction_source',
      }),
      field({
        key: 'ocr_confidence',
        label: 'OCR Confidence',
        officialName: 'OCR Confidence',
        type: 'number',
        aceMappingId: 'manifest1302.ocr_confidence',
        validation: [NUMERIC_RULE],
      }),
      field({
        key: 'system_confidence',
        label: 'System Confidence',
        officialName: 'System Confidence',
        type: 'number',
        aceMappingId: 'manifest1302.system_confidence',
        validation: [NUMERIC_RULE],
      }),
      field({
        key: 'agent_confirmed_status',
        label: 'Agent-Confirmed Status',
        officialName: 'Agent-Confirmed Status',
        type: 'select',
        options: YES_NO_OPTIONS,
        aceMappingId: 'manifest1302.agent_confirmed_status',
      }),
      field({
        key: 'last_edited_by',
        label: 'Last Edited By',
        officialName: 'Last Edited By',
        type: 'text',
        aceMappingId: 'manifest1302.last_edited_by',
      }),
      field({
        key: 'last_edited_timestamp',
        label: 'Last Edited Timestamp',
        officialName: 'Last Edited Timestamp',
        type: 'text',
        placeholder: 'YYYY-MM-DD HH:MM',
        aceMappingId: 'manifest1302.last_edited_timestamp',
      }),
      field({
        key: 'amendment_correction_history',
        label: 'Amendment / Correction History',
        officialName: 'Amendment / Correction History',
        type: 'textarea',
        aceMappingId: 'manifest1302.amendment_correction_history',
      }),
    ],
  },
]

export const MANIFEST_1302_SHIPMENT_FIELDS: DocumentFieldDefinition[] = [
  field({
    key: 'bill_of_lading_number',
    label: 'Bill of Lading Number',
    officialName: 'Bill of Lading Number',
    type: 'text',
    required: true,
    aceMappingId: 'manifest1302.shipment.bill_of_lading_number',
    helpText: 'Use the shipment bill number shown on the cargo declaration or supporting carrier document.',
    validation: [{ type: 'required', message: 'Bill of lading number is required.' }, BILL_RULE],
  }),
  field({
    key: 'master_bill_number',
    label: 'Master Bill Number',
    officialName: 'Master Bill Number',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.master_bill_number',
    validation: [BILL_RULE],
  }),
  field({
    key: 'house_bill_number',
    label: 'House Bill Number',
    officialName: 'House Bill Number',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.house_bill_number',
    validation: [BILL_RULE],
  }),
  field({
    key: 'bill_date',
    label: 'Bill Date',
    officialName: 'Bill Date',
    type: 'date',
    aceMappingId: 'manifest1302.shipment.bill_date',
    validation: [ISO_DATE_RULE],
  }),
  field({
    key: 'shipper_name',
    label: 'Shipper Name',
    officialName: 'Shipper Name',
    type: 'text',
    required: true,
    aceMappingId: 'manifest1302.shipment.shipper_name',
    validation: [{ type: 'required', message: 'Shipper name is required.' }],
  }),
  field({
    key: 'shipper_address',
    label: 'Shipper Address',
    officialName: 'Shipper Address',
    type: 'textarea',
    aceMappingId: 'manifest1302.shipment.shipper_address',
  }),
  field({
    key: 'consignee_name',
    label: 'Consignee Name',
    officialName: 'Consignee Name',
    type: 'text',
    required: true,
    aceMappingId: 'manifest1302.shipment.consignee_name',
    validation: [{ type: 'required', message: 'Consignee name is required.' }],
  }),
  field({
    key: 'consignee_address',
    label: 'Consignee Address',
    officialName: 'Consignee Address',
    type: 'textarea',
    aceMappingId: 'manifest1302.shipment.consignee_address',
  }),
  field({
    key: 'notify_party_name',
    label: 'Notify Party Name',
    officialName: 'Notify Party',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.notify_party_name',
  }),
  field({
    key: 'notify_party_address',
    label: 'Notify Party Address',
    officialName: 'Notify Party Address',
    type: 'textarea',
    aceMappingId: 'manifest1302.shipment.notify_party_address',
  }),
  field({
    key: 'marks_and_numbers',
    label: 'Marks and Numbers',
    officialName: 'Marks and Numbers',
    type: 'textarea',
    aceMappingId: 'manifest1302.shipment.marks_and_numbers',
  }),
  field({
    key: 'container_numbers',
    label: 'Container Number(s)',
    officialName: 'Container Number(s)',
    type: 'text',
    placeholder: 'ABCD1234567, EFGH7654321',
    aceMappingId: 'manifest1302.shipment.container_numbers',
    validation: [CONTAINER_RULE],
  }),
  field({
    key: 'seal_numbers',
    label: 'Seal Number(s)',
    officialName: 'Seal Number(s)',
    type: 'text',
    placeholder: 'SEAL001, SEAL002',
    aceMappingId: 'manifest1302.shipment.seal_numbers',
  }),
  field({
    key: 'number_of_packages',
    label: 'Number of Packages',
    officialName: 'Number of Packages',
    type: 'number',
    aceMappingId: 'manifest1302.shipment.number_of_packages',
    validation: [NUMERIC_RULE],
  }),
  field({
    key: 'kind_of_packages',
    label: 'Kind of Packages',
    officialName: 'Kind of Packages',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.kind_of_packages',
  }),
  field({
    key: 'goods_description',
    label: 'Description of Goods',
    officialName: 'Description of Goods',
    type: 'textarea',
    required: true,
    aceMappingId: 'manifest1302.shipment.goods_description',
    validation: [{ type: 'required', message: 'Description of goods is required.' }],
  }),
  field({
    key: 'gross_weight',
    label: 'Gross Weight',
    officialName: 'Gross Weight',
    type: 'number',
    aceMappingId: 'manifest1302.shipment.gross_weight',
    validation: [NUMERIC_RULE],
  }),
  field({
    key: 'weight_unit',
    label: 'Weight Unit',
    officialName: 'Weight Unit',
    type: 'select',
    options: MANIFEST_1302_WEIGHT_UNIT_OPTIONS,
    aceMappingId: 'manifest1302.shipment.weight_unit',
  }),
  field({
    key: 'measurement_volume',
    label: 'Measurement / Volume',
    officialName: 'Measurement',
    type: 'number',
    aceMappingId: 'manifest1302.shipment.measurement_volume',
    validation: [NUMERIC_RULE],
  }),
  field({
    key: 'first_port_place_possession',
    label: 'First Port / Place Where Carrier Took Possession',
    officialName: 'First Port/Place Where Carrier Took Possession of the Cargo',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.first_port_place_possession',
  }),
  field({
    key: 'country_of_origin',
    label: 'Country of Origin',
    officialName: 'Country of Origin',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.country_of_origin',
  }),
  field({
    key: 'country_of_export',
    label: 'Country of Export',
    officialName: 'Country of Export',
    type: 'text',
    aceMappingId: 'manifest1302.shipment.country_of_export',
  }),
  field({
    key: 'hazardous_indicator',
    label: 'Hazardous or Special Cargo Indicator',
    officialName: 'Hazardous or Special Cargo Indicator',
    type: 'select',
    options: YES_NO_OPTIONS,
    aceMappingId: 'manifest1302.shipment.hazardous_indicator',
  }),
  field({
    key: 'refrigerated_indicator',
    label: 'Refrigerated / Special Handling Indicator',
    officialName: 'Refrigerated / Special Handling Indicator',
    type: 'select',
    options: YES_NO_OPTIONS,
    aceMappingId: 'manifest1302.shipment.refrigerated_indicator',
  }),
  field({
    key: 'shipment_reconciliation_status',
    label: 'Cross-Document Match Status',
    officialName: 'Cross-Document Match Status',
    type: 'select',
    options: MANIFEST_1302_DISCREPANCY_OPTIONS,
    aceMappingId: 'manifest1302.shipment.reconciliation_status',
  }),
]

export const MANIFEST_1302_FIELD_REGISTRY = [
  ...MANIFEST_1302_SECTIONS.flatMap((section) =>
    section.kind === 'line_items'
      ? MANIFEST_1302_SHIPMENT_FIELDS.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        }))
      : section.fields.map((fieldDefinition) => ({
          ...fieldDefinition,
          sectionId: section.id,
          sectionTitle: section.title,
        })),
  ),
]
